import { Component, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { catchError, forkJoin, of, Subscription } from 'rxjs';
import { flatMap as _flatmap } from 'lodash';
import { factory } from '@/app/shared/app-logging';
import { TemplatesPriorityService } from '@/app/services/template-priority.service';
import { CustomPriorityByEventType, EventTypeModel, TemplateModel, TemplatePriorityLog, TemplatePriorityModel } from '@/app/model/template.priority.model';
import { DropdownEntry } from '@/app/model/dropdown-entry';
import { UserService } from '@/app/services/user.service';
import { User } from '@/app/model/user';

const logger = factory.getLogger('TemplatePriorityTemplatesComponent');

@Component({
  selector: 'app-template-priority-templates',
  templateUrl: './templates.component.html',
  styleUrls: ['./templates.component.scss']
})
export class TemplatePriorityTemplatesComponent implements OnInit, OnDestroy {

  private subscriptions$: Subscription[] = [];
  private timeoutIds$: any[] = [];

  @Input() lengthByScreen: number = 4;

  public classFilter = "visually-hidden";
  public eventTypes: EventTypeModel[] = [];
  public eventTypeCodes: string[] = [];
  public templates: TemplateModel[] = [];
  public templateNames: string[] = [];
  public defaultTemplatePriorities: TemplatePriorityModel[] = [];
  public templatePriorities: TemplatePriorityModel[] = [];
  public editedTemplatePriorities: TemplatePriorityModel[] = [];

  public templatesListIsLoading: boolean = false;
  public editEnabled: boolean = false;
  public isLoadingActive: boolean = false;
  public openModalClearAllStatus: boolean = false;

  public dropdownSelectedEntries: Array<DropdownEntry> = [];

  public templatePriorityLogInfo: TemplatePriorityLog = {
    username: '',
    user_id: '',
    template_name:  '',
    old_priority: '',
    new_priority: '',
  };

  constructor(
    public templatePriorityService: TemplatesPriorityService,
    public userService: UserService
  ) {

  }

  ngOnInit() {
    // subscribe to template priority state changes
    this.subscriptions$.push(this.templatePriorityService.getAction().subscribe((value) => {
      if (value === 'EDIT') {
        this.editEnabled = true;
      }
      if (value === 'CANCEL') {
        this.templatePriorityService.setTemplatePriorities(this.templatePriorities);
        this.editedTemplatePriorities = JSON.parse(JSON.stringify(this.templatePriorities));
        this.templatePriorityService.setEditedTemplatePriorities(this.editedTemplatePriorities);
        this.editEnabled = false;
      }
      if (value === 'SAVE_OK') {
        this.templatePriorities = JSON.parse(JSON.stringify(this.editedTemplatePriorities));
        this.templatePriorityService.setTemplatePriorities(this.templatePriorities);
        this.editEnabled = false;
      }
    }));
    // subscribe to template priority isLoadingActive changes
    this.subscriptions$.push(this.templatePriorityService.getIsLoadingActive().subscribe((value) => {
      this.isLoadingActive = value;
    }));
    // subscribe to template priority selected OPPs changes
    this.subscriptions$.push(this.templatePriorityService.getDropdownSelectedEntries().subscribe((value) => {
      if (JSON.stringify(this.dropdownSelectedEntries) !== JSON.stringify(value)) {
        this.dropdownSelectedEntries = value;
        if (!this.dropdownSelectedEntries || this.dropdownSelectedEntries.length == 0
          || (this.dropdownSelectedEntries.length == 1 && (this.dropdownSelectedEntries[0].product?.id == null || this.dropdownSelectedEntries[0].product?.id == 'ALL'))
          || (!this.defaultTemplatePriorities || this.defaultTemplatePriorities.length == 0)) {
          // RESET to default priorities
          this.templatePriorities = JSON.parse(JSON.stringify(this.defaultTemplatePriorities));
          this.editedTemplatePriorities = JSON.parse(JSON.stringify(this.defaultTemplatePriorities));
          this.templatePriorityService.setTemplatePriorities(this.templatePriorities);
          this.templatePriorityService.setEditedTemplatePriorities(this.editedTemplatePriorities);
          this._retrieveTemplatePriorities();
        } else {
          if (this.dropdownSelectedEntries.length >= 1) {
            // ASK for custom priorities            
            // It's not needed to call it again if the end user selects more than one product
            this._retrieveTemplateCustomPriorities();
          }
        }
      }
    }));

    this.retrieveEventTypesAndTemplates();
  }

  getUserDetails() {
    this.subscriptions$.push(this.userService.getUserDetailData().subscribe((value: User) => {
      if (value) {
        if (value.userDetails.user_id && value.userDetails.username) {
          this.templatePriorityLogInfo.user_id = value.userDetails.user_id;
          this.templatePriorityLogInfo.username = value.userDetails.username;
        }
      }
    }));
  }
  
  ngOnDestroy(): void {
    this.subscriptions$.forEach(sub => sub.unsubscribe());
    this.timeoutIds$.forEach(timeoutId => clearTimeout(timeoutId));
  }

  private retrieveEventTypesAndTemplates() {
    logger.info('Retrieving Event Types and Templates...');

    this.templatesListIsLoading = true

    forkJoin([
      this.templatePriorityService.retrieveEventTypes().pipe(
        catchError((err: any) => {
          this.templatePriorityService.setError(err?.error?.message);
          return of(err);
        })
      ),
      this.templatePriorityService.retrieveTemplatesNames().pipe(
        catchError((err: any) => {
          this.templatePriorityService.setError(err?.error?.message);
          return of(err);
        })
      ),
    ]).subscribe(([eventTypes, templates]) => {
      if (eventTypes && templates) {
        this.eventTypes = eventTypes;
        this.eventTypeCodes = eventTypes.map((type: any) => type.code);
        this.templates = templates;
        this.templateNames = templates.map((type: any) => type.name);
        this._retrieveTemplatePriorities();
      }
    });
  }

  private _retrieveTemplatePriorities(product_id?: string) {
    if(this.eventTypeCodes && this.eventTypeCodes.length > 0 && this.templateNames && this.templateNames.length > 0) {
      this.templatesListIsLoading = true;
      this.templatePriorityService.setIsLoadingActive(true);
      this.templatePriorityService
      .retrieveTemplatePriorities(this.eventTypeCodes, this.templates, product_id)
      .pipe(
        catchError((err: any) => {
          this.templatePriorityService.setError(err?.error?.message);
          return of(err);
        })
      )
      .subscribe((response: TemplatePriorityModel[]) => {
        if (response && response.length > 0) {
          if (!product_id) {
            this.templatePriorityService.getTemplatesWithByEventTypesEnabled().forEach((template_name: string) => {
              const itemsForTemplateWithByEventTypesEnabled: TemplatePriorityModel[] = response.filter((res: any) => res.template_name === template_name);
              if (itemsForTemplateWithByEventTypesEnabled && itemsForTemplateWithByEventTypesEnabled.length > 1) {
                // remove the items from response
                response = response.filter((res: any) => res.template_name !== template_name);
                // Build one unique item with a customization array
                const newUniqueTemplatePriority: TemplatePriorityModel = JSON.parse(JSON.stringify(itemsForTemplateWithByEventTypesEnabled[0]));
                newUniqueTemplatePriority.customizations = [];
                newUniqueTemplatePriority.event_types = this.eventTypeCodes;
                const customizationPriorities: CustomPriorityByEventType[] = [];
                itemsForTemplateWithByEventTypesEnabled.forEach((itemForTemplateWithByEventTypesEnabled: TemplatePriorityModel) => {
                  itemForTemplateWithByEventTypesEnabled.event_types.forEach((eventType: string) => {
                    customizationPriorities.push({
                      event_type: eventType,
                      priority: itemForTemplateWithByEventTypesEnabled.priority
                    });
                  });
                });
                newUniqueTemplatePriority.customizations = customizationPriorities;
                response.push(newUniqueTemplatePriority);
              }
            });
            response.sort((a: TemplatePriorityModel, b: TemplatePriorityModel) => {
              const nameA = a.template_name;
              const nameB = b.template_name;
              if (nameA < nameB) {
                return -1;
              }
              if (nameA > nameB) {
                return 1;
              }
              return 0;
            });
          }
          this.defaultTemplatePriorities = JSON.parse(JSON.stringify(response.sort((a,b) => a.template_display_label > b.template_display_label ? 1 : -1)));
          this.templatePriorities = JSON.parse(JSON.stringify(response.sort((a,b) => a.template_display_label > b.template_display_label ? 1 : -1)));
          this.editedTemplatePriorities = JSON.parse(JSON.stringify(response.sort((a,b) => a.template_display_label > b.template_display_label ? 1 : -1)));
          this.templatePriorityService.setDefaultTemplatePriorities(this.defaultTemplatePriorities);
          this.templatePriorityService.setTemplatePriorities(this.templatePriorities);
          this.templatePriorityService.setEditedTemplatePriorities(this.editedTemplatePriorities);
          this.templatesListIsLoading = false;
          this.templatePriorityService.setIsLoadingActive(false);
        } else {

        }
      });
    }
  }

  private _retrieveTemplateCustomPriorities() {
    const product_ids: string[] = this.dropdownSelectedEntries
      .map((dropdownEntry: DropdownEntry) => dropdownEntry.product.id);
    if (product_ids && product_ids.length > 0 && product_ids[0] && this.eventTypeCodes && this.eventTypeCodes.length > 0 && this.templateNames && this.templateNames.length > 0) {
      // set loading
      this.templatesListIsLoading = true;
      this.templatePriorityService.setIsLoadingActive(true);
      // retrieve template priorities customizations for the selected product
      forkJoin([
        this.templatePriorityService.retrieveTemplatePriorities(this.eventTypeCodes, this.templates, product_ids[0])
        .pipe(
          catchError((err: any) => {
            this.templatePriorityService.setError(err?.error?.message);
            return of(err);
          })
        )
      ]).subscribe(([templatePrioritiesFromServer]) => {
        // reset to default priorities without customizations
        this.templatePriorities = JSON.parse(JSON.stringify(this.defaultTemplatePriorities));
        // reset customizations and event types
        this.templatePriorities.forEach((templatePriority: TemplatePriorityModel) => {
          templatePriority.customizations = [];
          templatePriority.event_types = this.eventTypeCodes;
        });
        // if template priorities found for the selected product
        if (templatePrioritiesFromServer && templatePrioritiesFromServer.length > 0) {
          // scan template priorities
          templatePrioritiesFromServer.forEach((templatePriorityFromServer: TemplatePriorityModel) => {
            if (!this.templatePriorityService.getTemplatesWithByEventTypesEnabled().includes(templatePriorityFromServer.template_name)) {
              // if template priority is not configured to have "by event types" enabled
              // set the priority
              this.templatePriorities.find((tp: TemplatePriorityModel) => tp.template_name == templatePriorityFromServer.template_name).priority = templatePriorityFromServer.priority;
            } else {
              // if template priority is configured to have "by event types" enabled
              // create custom priority by event type object for each event type
              const templatePriority: TemplatePriorityModel = this.templatePriorities.find((tp: TemplatePriorityModel) => tp.template_name == templatePriorityFromServer.template_name);
              
              // set new values
              if (templatePriorityFromServer.event_types.length == this.eventTypeCodes.length) {
                templatePriority.priority = templatePriorityFromServer.priority;
              } else {
                templatePriorityFromServer.event_types.forEach((eventType: string) => {
                  templatePriority.customizations.push({
                    event_type: eventType,
                    priority: templatePriorityFromServer.priority
                  });
                });
              }
            }
          });
        }
        
        // set missing customizations
        this.templatePriorities
          .filter((templatePriority: TemplatePriorityModel) => templatePriority.customizations?.length > 0)
          .forEach((templatePriority: TemplatePriorityModel) => {
            if (templatePriority.customizations?.length < this.eventTypeCodes.length) {
              this.eventTypeCodes
                .filter((eventType: string) => !templatePriority.customizations.map((customization: CustomPriorityByEventType) => customization.event_type).includes(eventType))
                .forEach((eventType: string) => {
                  templatePriority.customizations.push({
                    event_type: eventType,
                    priority: templatePriority.priority
                  });
                });
            }
          });

        this.templatePriorityService.setTemplatePriorities(this.templatePriorities);
        this.editedTemplatePriorities = JSON.parse(JSON.stringify(this.templatePriorities));
        this.templatePriorityService.setEditedTemplatePriorities(this.editedTemplatePriorities);
        this.templatesListIsLoading = false;
        this.templatePriorityService.setIsLoadingActive(false);
      });
    }
  }

  closeModalClearAll(event: any) {
    if (event) {
      this.templatesListIsLoading = true;
      this.templatePriorityService.setIsLoadingActive(true);
      this.editedTemplatePriorities.forEach(
        (templatePriority: TemplatePriorityModel) => {
          templatePriority.priority = 'LOW';
          templatePriority.customizations = [];
        }
      );
      this.templatePriorityService.setEditedTemplatePriorities(this.editedTemplatePriorities);
      this.templatePriorityService.setIsSaveEnabled(JSON.stringify(this.templatePriorities) !== JSON.stringify(this.editedTemplatePriorities));
      this.timeoutIds$.push(setTimeout(() => {
        this.templatesListIsLoading = false;
        this.templatePriorityService.setIsLoadingActive(false);
      }, 1000));
    }
    this.openModalClearAllStatus = false;
  }

  openModalClearAll() {
    this.openModalClearAllStatus = true;
  }

  onUpdateTemplatePriority(event) {
    this.templatePriorityLogInfo = {
      username: '',
      user_id: '',
      template_name:  '',
      old_priority: '',
      new_priority: '',
    };
    this.getUserDetails();
    this.templatePriorityLogInfo.template_name = event.template_name;
    this.templatePriorityLogInfo.old_priority = event.old_priority;
    this.templatePriorityLogInfo.new_priority = event.priority;
    this.templatePriorityService.loggedTemplatePriorities.update((value) => [...value, this.templatePriorityLogInfo]);
    
    this.editedTemplatePriorities.forEach(
      (templatePriority: TemplatePriorityModel) => {
        if (templatePriority.template_name == event.template_name) {
          templatePriority.priority = event.priority;
          templatePriority.customizations = event.customizations;
        }
      }
    );
    this.templatePriorityService.setEditedTemplatePriorities(this.editedTemplatePriorities);
    this.templatePriorityService.setIsSaveEnabled(JSON.stringify(this.templatePriorities) !== JSON.stringify(this.editedTemplatePriorities));
  }
}
